/**
 * Team Chat Component - Private chat room for outing teams
 */
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Send, Image, Camera, Smile, Users, ChevronDown, 
  MessageCircle, Check, CheckCheck
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';

// Message Bubble Component
const MessageBubble = ({ message, isOwn, showAvatar }) => {
  const initials = message.sender_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??';
  
  return (
    <div className={`flex gap-2 mb-3 ${isOwn ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {showAvatar && !isOwn && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2A5A3B] to-[#1A3A2B] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          {message.sender_avatar ? (
            <img src={message.sender_avatar} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            initials
          )}
        </div>
      )}
      {!showAvatar && !isOwn && <div className="w-8" />}
      
      {/* Message Content */}
      <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
        {/* Sender name for others */}
        {showAvatar && !isOwn && (
          <p className="text-xs text-zinc-500 mb-1 ml-1">{message.sender_name}</p>
        )}
        
        <div className={`rounded-2xl px-4 py-2 ${
          isOwn 
            ? 'bg-[#007AFF] text-white rounded-br-md' 
            : 'bg-[#1A1A1A] text-white rounded-bl-md'
        }`}>
          {/* Image if present */}
          {message.image_url && (
            <img 
              src={message.image_url} 
              alt="" 
              className="rounded-lg mb-2 max-w-full"
            />
          )}
          
          {/* Text */}
          <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
        </div>
        
        {/* Time and status */}
        <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
          <span className="text-[10px] text-zinc-500">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          {isOwn && (
            message.read ? (
              <CheckCheck className="w-3 h-3 text-[#007AFF]" />
            ) : (
              <Check className="w-3 h-3 text-zinc-500" />
            )
          )}
        </div>
      </div>
    </div>
  );
};

// Score Alert Message
const ScoreAlert = ({ alert }) => (
  <div className="flex justify-center my-4">
    <div className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${
      alert.type === 'eagle' ? 'bg-amber-500/20 text-amber-400' :
      alert.type === 'birdie' ? 'bg-green-500/20 text-green-400' :
      alert.type === 'hole_in_one' ? 'bg-purple-500/20 text-purple-400 animate-pulse' :
      'bg-white/10 text-white'
    }`}>
      {alert.type === 'eagle' && '🦅'}
      {alert.type === 'birdie' && '🐦'}
      {alert.type === 'hole_in_one' && '🎯'}
      <span>{alert.player_name} {alert.type === 'hole_in_one' ? 'HOLE IN ONE!' : alert.type.toUpperCase()} on Hole {alert.hole}!</span>
    </div>
  </div>
);

// Team Chat Main Component
export default function TeamChat({ 
  teamId, 
  teamName, 
  outingId, 
  currentUser, 
  isOpen, 
  onClose 
}) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Scroll to bottom on new messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load messages
  useEffect(() => {
    if (isOpen && teamId) {
      loadMessages();
      loadTeamMembers();
      
      // Poll for new messages every 5 seconds
      const interval = setInterval(loadMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, teamId]);

  const loadMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/outings/${outingId}/teams/${teamId}/chat`);
      const data = await res.json();
      if (data.success) {
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/outings/${outingId}/teams/${teamId}`);
      const data = await res.json();
      if (data.success) {
        setTeamMembers(data.team?.players || []);
      }
    } catch (err) {
      console.error('Failed to load team members:', err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/outings/${outingId}/teams/${teamId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_id: currentUser?.user_id,
          sender_name: currentUser?.name || 'Unknown',
          sender_avatar: currentUser?.avatar_url,
          text: newMessage.trim()
        })
      });
      
      const data = await res.json();
      if (data.success) {
        setNewMessage('');
        loadMessages();
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Group messages by sender to show/hide avatars
  const shouldShowAvatar = (message, index) => {
    if (index === 0) return true;
    const prevMessage = messages[index - 1];
    return prevMessage.sender_id !== message.sender_id;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col animate-fade-in">
      {/* Header */}
      <div className="bg-[#1A1A1A] border-b border-white/10 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
          <div>
            <h2 className="text-white font-bold">{teamName}</h2>
            <p className="text-xs text-zinc-400">{teamMembers.length} members</p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowMembers(!showMembers)}
          className="text-zinc-400"
        >
          <Users className="w-5 h-5" />
        </Button>
      </div>

      {/* Members Dropdown */}
      {showMembers && (
        <div className="bg-[#121212] border-b border-white/10 p-3">
          <p className="text-xs text-zinc-500 mb-2">TEAM MEMBERS</p>
          <div className="flex flex-wrap gap-2">
            {teamMembers.map((member, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/5 rounded-full px-3 py-1">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#2A5A3B] to-[#1A3A2B] flex items-center justify-center text-white text-[10px] font-bold">
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    member.name?.split(' ').map(n => n[0]).join('').slice(0, 2)
                  )}
                </div>
                <span className="text-white text-sm">{member.name}</span>
                {member.is_captain && (
                  <Badge className="bg-[#C9A227]/20 text-[#C9A227] text-[10px]">Captain</Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 bg-[#0A0A0A]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageCircle className="w-16 h-16 text-zinc-700 mb-4" />
            <h3 className="text-white font-bold mb-2">Team Chat</h3>
            <p className="text-zinc-500 text-sm max-w-xs">
              Coordinate with your team, share photos, and celebrate great shots!
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              msg.type === 'score_alert' ? (
                <ScoreAlert key={msg.id || i} alert={msg} />
              ) : (
                <MessageBubble
                  key={msg.id || i}
                  message={msg}
                  isOwn={msg.sender_id === currentUser?.user_id}
                  showAvatar={shouldShowAvatar(msg, i)}
                />
              )
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="bg-[#1A1A1A] border-t border-white/10 p-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-zinc-400 hover:text-white flex-shrink-0"
          >
            <Camera className="w-5 h-5" />
          </Button>
          
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Message your team..."
              className="bg-[#0D0D0D] border-white/10 text-white pr-10 h-10"
            />
            <button className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
              <Smile className="w-5 h-5" />
            </button>
          </div>
          
          <Button
            onClick={sendMessage}
            disabled={!newMessage.trim() || sending}
            className="bg-[#007AFF] hover:bg-[#0056b3] flex-shrink-0"
            size="icon"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
